import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Calendar, Tag } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { COLORS, IMAGES, fadeInUp, staggerContainer, scaleIn } from '../config';

const News = () => {
  const { language, t } = useLanguage();
  const [newsItems, setNewsItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch('/news.json')
      .then((res) => res.json())
      .then((data) => {
        setNewsItems(data);
        setLoading(false);
      })
      .catch(() => {
        setNewsItems([]);
        setLoading(false);
      });
  }, []);

  const getField = (item, field) => {
    return item[`${field}_${language}`] || item[`${field}_it`] || '';
  };

  return (
    <>
      {/* Hero Banner */}
      <section className="relative h-[50vh] flex items-center justify-center overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${IMAGES.restaurant})` }}
        />
        <div className="absolute inset-0" style={{ background: `linear-gradient(135deg, rgba(114, 47, 55, 0.9) 0%, rgba(30, 58, 95, 0.85) 100%)` }} />
        <div className="relative z-10 text-center px-4">
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            className="font-['Cormorant_Garamond'] text-5xl md:text-6xl text-white mb-4"
          >
            {t('newsPage.hero.title')}
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-xl italic"
            style={{ color: COLORS.gold }}
          >
            {t('newsPage.hero.subtitle')}
          </motion.p>
        </div>
      </section>

      {/* News Cards */}
      <section className="py-24" style={{ backgroundColor: COLORS.vaniglia }}>
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="text-center py-16">
              <p style={{ color: COLORS.redwine }}>Loading...</p>
            </div>
          ) : newsItems.length === 0 ? (
            <div className="text-center py-16">
              <p style={{ color: `${COLORS.nero}99` }}>{language === 'it' ? 'Nessuna news al momento.' : 'No news at the moment.'}</p>
            </div>
          ) : (
            <motion.div
              variants={staggerContainer}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              className="space-y-12"
            >
              {newsItems.map((item) => (
                <motion.article
                  key={item.id}
                  variants={scaleIn}
                  className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center rounded-2xl overflow-hidden shadow-lg"
                  style={{ backgroundColor: 'white' }}
                  data-testid={`news-card-${item.id}`}
                >
                  <div className="overflow-hidden h-72 lg:h-full flex items-center justify-center" style={{ backgroundColor: COLORS.vaniglia }}>
                    <img
                      src={item.immagine}
                      alt={getField(item, 'titolo')}
                      className="w-full h-full object-contain"
                    />
                  </div>
                  <div className="p-8 lg:p-10">
                    <div className="flex items-center gap-4 mb-4 flex-wrap">
                      <span
                        className="px-3 py-1 rounded-full text-xs font-medium inline-flex items-center gap-1"
                        style={{ backgroundColor: COLORS.sage, color: COLORS.navy }}
                      >
                        <Tag size={12} />
                        {getField(item, 'categoria')}
                      </span>
                      <span className="flex items-center gap-1 text-sm" style={{ color: `${COLORS.nero}99` }}>
                        <Calendar size={14} />
                        {getField(item, 'data')}
                      </span>
                    </div>
                    <h2 className="font-['Cormorant_Garamond'] text-3xl md:text-4xl mb-4" style={{ color: COLORS.redwine }}>
                      {getField(item, 'titolo')}
                    </h2>
                    <p className="leading-relaxed" style={{ color: `${COLORS.nero}b3` }}>
                      {getField(item, 'testo')}
                    </p>
                  </div>
                </motion.article>
              ))}
            </motion.div>
          )}
        </div>
      </section>
    </>
  );
};

export default News;
